import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';

interface Venue {
  id: string;
  name: string;
  address: string;
  timezone?: string;
  created?: string;
  updated?: string;
  company_id?: string;
  company_name?: string;
}

interface Company {
  company_id: string;
  company_name: string;
}

interface VenueLocation {
  location_id: string;
  name: string;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  lock_screen_message?: string;
}

const Venues: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [venues, setVenues] = useState<Venue[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [formData, setFormData] = useState({
    companyId: '',
    name: '',
    address: '',
    timezone: 'Europe/Belgrade',
    latitude: '',
    longitude: '',
    altitude: ''
  });

  // Define fetchVenues before useEffect hooks
  const fetchVenues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Add search parameter if provided
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await api.get('/productions/venues', { params });
      // PassKit returns {venues: [], total: 0} format
      setVenues(response.data.venues || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load venues');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchVenues();
    fetchLocations();
    if (isAdmin) {
      fetchCompanies();
    }
  }, [isAdmin, fetchVenues]);

  // Debounce search: trigger fetchVenues when searchTerm changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchVenues();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm, fetchVenues]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/membership/companies');
      // API returns {companies: [...]} format
      setCompanies(response.data?.companies || []);
    } catch (err: any) {
      logger.error('Failed to fetch companies:', err);
      setCompanies([]); // Set empty array on error
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations/');
      // API returns array of locations with GPS data from PassKit
      setLocations(response.data || []);
    } catch (err: any) {
      logger.error('Failed to fetch locations:', err);
      setLocations([]);
    }
  };

  const handleOpenDialog = (venue?: Venue) => {
    if (venue) {
      setSelectedVenue(venue);
      setFormData({
        companyId: '',
        name: venue.name,
        address: venue.address,
        timezone: venue.timezone || 'Europe/Belgrade',
        latitude: '',
        longitude: '',
        altitude: ''
      });
    } else {
      setSelectedVenue(null);
      setFormData({
        companyId: isAdmin && companies.length > 0 ? companies[0].company_id : '',
        name: '',
        address: '',
        timezone: 'Europe/Belgrade',
        latitude: '',
        longitude: '',
        altitude: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVenue(null);
  };

  const handleLocationSelect = (locationId: string) => {
    const location = locations.find(loc => loc.location_id === locationId);
    if (location) {
      setFormData({
        ...formData,
        latitude: location.latitude?.toString() || '',
        longitude: location.longitude?.toString() || '',
        altitude: location.altitude?.toString() || ''
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      // Build PassKit-compatible venue object
      const venuePayload: any = {
        name: formData.name,
        address: formData.address
      };

      // Add optional timezone
      if (formData.timezone) {
        venuePayload.timezone = formData.timezone;
      }

      // Add gpsCoords if latitude and longitude provided
      if (formData.latitude && formData.longitude) {
        venuePayload.gpsCoords = [
          {
            name: formData.name,
            lat: Number.parseFloat(formData.latitude),
            lon: Number.parseFloat(formData.longitude),
            ...(formData.altitude && { alt: Number.parseFloat(formData.altitude) })
          }
        ];
      }

      // For admin creating new venue, include companyId
      if (isAdmin && !selectedVenue && formData.companyId) {
        venuePayload.companyId = formData.companyId;
      }

      if (selectedVenue) {
        await api.patch(`/productions/venues/${selectedVenue.id}`, venuePayload);
      } else {
        await api.post('/productions/venues', venuePayload);
      }

      await fetchVenues();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save venue');
    }
  };

  const handleDelete = async () => {
    if (!selectedVenue) return;

    setError(null);
    try {
      await api.delete(`/productions/venues/${selectedVenue.id}`);

      await fetchVenues();
      setDeleteDialogOpen(false);
      setSelectedVenue(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete venue');
    }
  };

  const openDeleteDialog = (venue: Venue) => {
    setSelectedVenue(venue);
    setDeleteDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Mesta održavanja (Venues)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo mesto
        </Button>
      </Box>

      {/* Search Box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Pretraži mesta"
          placeholder="Pretraži po nazivu, adresi ili kompaniji..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          helperText="Pretraga je automatska (500ms odlaganje)"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Naziv</strong></TableCell>
                <TableCell><strong>Adresa</strong></TableCell>
                <TableCell><strong>Timezone</strong></TableCell>
                {isAdmin && <TableCell><strong>Kompanija</strong></TableCell>}
                <TableCell align="right"><strong>Akcije</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {venues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Nema unetih mesta. Kliknite na "Novo mesto" da dodate prvo.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                venues.map((venue) => (
                  <TableRow key={venue.id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">{venue.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{venue.address || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{venue.timezone || '-'}</Typography>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Typography variant="body2">{venue.company_name || '-'}</Typography>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <IconButton
                        size="small"                        onClick={() => handleOpenDialog(venue)}
                        color="primary"
                        title="Izmeni mesto"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"                        onClick={() => openDeleteDialog(venue)}
                        color="error"
                        title="Obriši mesto"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedVenue ? 'Izmena mesta' : 'Novo mesto'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {isAdmin && !selectedVenue && (
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Kompanija</InputLabel>
                  <Select
                    name="companyId"
                    value={formData.companyId}
                    label="Kompanija"
                    onChange={(e) => setFormData({...formData, companyId: e.target.value})}
                    required
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.company_id} value={company.company_id}>
                        {company.company_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Naziv mesta"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                helperText="Naziv lokacije (npr. 'Stark Arena', 'Dom omladine')"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Adresa"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                multiline
                rows={3}
                helperText="Kompletan address venue-a (npr. 'Bulevar Arsenija Čarnojevića 58, Beograd 11070, Serbia')"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                helperText="Vremenska zona (npr. Europe/Belgrade, America/New_York)"
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}>
                GPS Koordinate (opciono)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Za geofencing i prikaz na mapi u Wallet aplikaciji
              </Typography>
            </Grid>
            {locations.length > 0 && (
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Kopiraj iz postojeće lokacije</InputLabel>
                  <Select
                    label="Kopiraj iz postojeće lokacije"
                    onChange={(e) => handleLocationSelect(e.target.value)}
                    defaultValue=""
                  >
                    <MenuItem value="">
                      <em>-- Izaberi lokaciju --</em>
                    </MenuItem>
                    {locations.map((loc) => (
                      <MenuItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={6}>
              <TextField
                fullWidth
                label="Latitude (širina)"
                name="latitude"
                type="number"
                value={formData.latitude}
                onChange={handleInputChange}
                helperText="Npr. 44.8125 (Beograd)"
                inputProps={{ step: "0.000001" }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Longitude (dužina)"
                name="longitude"
                type="number"
                value={formData.longitude}
                onChange={handleInputChange}
                helperText="Npr. 20.4612 (Beograd)"
                inputProps={{ step: "0.000001" }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Altitude (visina)"
                name="altitude"
                type="number"
                value={formData.altitude}
                onChange={handleInputChange}
                helperText="Visina u metrima (opciono, npr. 117)"
                inputProps={{ step: "1" }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Otkaži</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedVenue ? 'Sačuvaj' : 'Kreiraj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Brisanje mesta</DialogTitle>
        <DialogContent>
          <Typography>
            Da li ste sigurni da želite da obrišete mesto "{selectedVenue?.name}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Svi događaji vezani za ovo mesto će takođe biti obrisani!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Otkaži</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Obriši
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Venues;
